
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

import { AppContext } from "../../App";
import { api, endpoints, controller } from "../../api/actions/api";
import { formatCurrency } from "../../hooks/Currency";
import ReportPrintButton from "../reports/ReportPrintButton";

const CustomerBalanceReport = (props) => {
  const { t } = useTranslation();
  const { _store } = props;

  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);

  const [report, setReport] = useState([]);
  const reportRef = useRef(null);

  useEffect(() => {
    if (!_store?.id) {
      setReport([]);
      return;
    }

    setLoader(true);

    api(
      `${endpoints.Customer}${controller.CustomerBalanceReport}/${_store.id}`
    )
      .get()
      .then((res) => {
        setLoader(false);

        if (res?.data?.status === 200) {
          setReport(res.data.result || []);
        } else {
          setReport([]);
        }
      })
      .catch((err) => {
        setLoader(false);
        console.error("Customer Balance Report:", err);
        setReport([]);
      });
  }, [_store?.id, setLoader]);

  const totalPurchase = report.reduce(
    (total, customer) =>
      total + Number(customer.totalPurchase || 0),
    0
  );

  const totalPaid = report.reduce(
    (total, customer) =>
      total + Number(customer.totalPaid || 0),
    0
  );

  const totalBalance = report.reduce(
    (total, customer) =>
      total + Number(customer.balance || 0),
    0
  );

  return (
    <Box>
      {/* Printable report area */}
      <Box
        ref={reportRef}
        className="customer-balance-report-print"
      >
        <Card>
          <CardContent>

            {/* Report header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
                mb: 2,
              }}
            >
              <Typography variant="h5">
                {t("customerBalanceReport")}
              </Typography>

              {/* Hidden when printing */}
              <Box className="no-print">
                <ReportPrintButton
                  reportRef={reportRef}
                  paperSize="Letter"
                  reportTitle={t("customerBalanceReport")}
                />
              </Box>
            </Box>

            {/* Customer balance table */}
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("customer")}
                    </TableCell>

                    <TableCell align="right">
                      {t("totalPurchase")}
                    </TableCell>

                    <TableCell align="right">
                      {t("totalPaid")}
                    </TableCell>

                    <TableCell align="right">
                      {t("currentBalance")}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {report.map((customer) => (
                    <TableRow key={customer.customerId}>
                      <TableCell>
                        <Button
                          variant="text"
                          size="small"
                          sx={{
                            p: 0,
                            minWidth: 0,
                            textTransform: "none",
                          }}
                          onClick={() => {
                            navigate(
                              `/customers/customer-report/${customer.customerId}`
                            );
                          }}
                        >
                          {customer.customerName}
                        </Button>
                      </TableCell>

                      <TableCell align="right">
                        {formatCurrency(
                          customer.totalPurchase,
                          _store
                        )}
                      </TableCell>

                      <TableCell align="right">
                        {formatCurrency(
                          customer.totalPaid,
                          _store
                        )}
                      </TableCell>

                      <TableCell align="right">
                        {formatCurrency(
                          customer.balance,
                          _store
                        )}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Total row */}
                  <TableRow
                    sx={{
                      bgcolor: "var(--primaryTXS-color)",
                    }}
                  >
                    <TableCell>
                      <strong>{t("total")}</strong>
                    </TableCell>

                    <TableCell align="right">
                      <strong>
                        {formatCurrency(
                          totalPurchase,
                          _store
                        )}
                      </strong>
                    </TableCell>

                    <TableCell align="right">
                      <strong>
                        {formatCurrency(
                          totalPaid,
                          _store
                        )}
                      </strong>
                    </TableCell>

                    <TableCell align="right">
                      <strong>
                        {formatCurrency(
                          totalBalance,
                          _store
                        )}
                      </strong>
                    </TableCell>
                  </TableRow>

                  {report.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        align="center"
                      >
                        {t("noData")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  _store: state.pos.defStore,
});

export default connect(
  mapStateToProps
)(CustomerBalanceReport);
